import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Horizontal } from 'atoms/layout/flex';
import { BodyM, HeadlineXS } from 'atoms/typography';
import {
  API_TERMS,
  BLOG_SPORT,
  CAREERS,
  COOKIE_POLICY,
  COVERAGE,
  CREATOR_PROGRAM,
  FAQ,
  FOOTBALL,
  FOOTBALL_MARKET,
  GAME_RULES,
  HELP,
  LEGAL_NOTICE,
  LICENSED_PARTNERS,
  MARKETPLACE_TERMS,
  MLB,
  MLB_MARKET,
  NBA,
  NBA_MARKET,
  PRESS,
  PRIVACY_POLICY,
  TERMS_AND_CONDITIONS,
} from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import { useInviteLink } from 'hooks/useInviteLink';
import { useEvents } from 'lib/events/useEvents';
import { navLabels } from 'lib/glossary';
import { sportToBlogSportMap } from 'lib/routing';
import { tabletAndAbove } from 'style/mediaQuery';

const FooterLink = styled(BodyM)`
  display: block;
  padding: var(--half-unit) 0;
  color: var(--c-nd-400);
  &:hover,
  &:focus {
    color: var(--c-white);
    text-decoration: underline;
  }
  @media ${tabletAndAbove} {
    color: var(--c-white);
  }
`;

const MenuItems = styled(Horizontal).attrs({
  gap: 8,
})`
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: var(--triple-unit) var(--unit);
`;

const FooterTitle = styled(HeadlineXS).attrs({
  as: 'h4',
})`
  margin-bottom: var(--half-unit);
`;

export const FooterLinks = () => {
  const { sport } = useSportContext();
  const track = useEvents();
  const trackLink = (source: string) => {
    track('Click Footer link', {
      source,
    });
  };
  const { formatMessage } = useIntl();
  const inviteLink = useInviteLink();

  return (
    <MenuItems>
      <div>
        <FooterTitle>
          <FormattedMessage
            id="Footer.title.helpAndResources"
            defaultMessage="Help & Resources"
          />
        </FooterTitle>
        <FooterLink
          as={Link}
          to={generatePath(BLOG_SPORT, {
            sport: sportToBlogSportMap[sport],
          })}
          onClick={() =>
            trackLink(
              generatePath(BLOG_SPORT, {
                sport: sport.toLowerCase(),
              })
            )
          }
        >
          {formatMessage(navLabels.blog)}
        </FooterLink>
        <FooterLink as={Link} to={HELP} onClick={() => trackLink(HELP)}>
          {formatMessage(navLabels.help)}
        </FooterLink>
        <FooterLink as={Link} to={FAQ} onClick={() => trackLink(FAQ)}>
          {formatMessage(navLabels.faq)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={GAME_RULES}
          onClick={() => trackLink(GAME_RULES)}
        >
          {formatMessage(navLabels.gameRules)}
        </FooterLink>
      </div>
      <div>
        <FooterTitle>
          <FormattedMessage
            id="Footer.title.termsAndPolicies"
            defaultMessage="Terms & Policies"
          />
        </FooterTitle>
        <FooterLink
          as={Link}
          to={TERMS_AND_CONDITIONS}
          onClick={() => trackLink(TERMS_AND_CONDITIONS)}
        >
          {formatMessage(navLabels.terms)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={MARKETPLACE_TERMS}
          onClick={() => trackLink(MARKETPLACE_TERMS)}
        >
          {formatMessage(navLabels.marketplaceTerms)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={API_TERMS}
          onClick={() => trackLink(API_TERMS)}
        >
          {formatMessage(navLabels.apiTerms)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={PRIVACY_POLICY}
          onClick={() => trackLink(PRIVACY_POLICY)}
        >
          {formatMessage(navLabels.privacyPolicy)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={COOKIE_POLICY}
          onClick={() => trackLink(COOKIE_POLICY)}
        >
          {formatMessage(navLabels.cookiePolicy)}
        </FooterLink>
      </div>
      <div>
        <FooterTitle>
          <FormattedMessage
            id="Footer.title.fantasyGames"
            defaultMessage="Sorare Fantasy Sports"
          />
        </FooterTitle>
        <FooterLink as={Link} to={FOOTBALL} onClick={() => trackLink(FOOTBALL)}>
          {formatMessage(navLabels.footballFantasy)}
        </FooterLink>
        <FooterLink as={Link} to={MLB} onClick={() => trackLink(MLB)}>
          {formatMessage(navLabels.mlbFantasy)}
        </FooterLink>
        <FooterLink as={Link} to={NBA} onClick={() => trackLink(NBA)}>
          {formatMessage(navLabels.nbaFantasy)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={FOOTBALL_MARKET}
          onClick={() => trackLink(FOOTBALL_MARKET)}
        >
          {formatMessage(navLabels.footballMarket)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={MLB_MARKET}
          onClick={() => trackLink(MLB_MARKET)}
        >
          {formatMessage(navLabels.mlbMarket)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={NBA_MARKET}
          onClick={() => trackLink(NBA_MARKET)}
        >
          {formatMessage(navLabels.nbaMarket)}
        </FooterLink>
      </div>
      <div>
        <FooterTitle>
          <FormattedMessage
            id="Footer.title.company"
            defaultMessage="About Sorare"
          />
        </FooterTitle>
        <FooterLink as={Link} to={CAREERS} onClick={() => trackLink(CAREERS)}>
          {formatMessage(navLabels.careers)}
        </FooterLink>

        <FooterLink
          as={Link}
          to={CREATOR_PROGRAM}
          onClick={() => trackLink(CREATOR_PROGRAM)}
        >
          {formatMessage(navLabels.creatorProgram)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={inviteLink}
          onClick={() => trackLink(inviteLink)}
        >
          {formatMessage(navLabels.inviteFriends)}
        </FooterLink>
        <FooterLink as={Link} to={PRESS} onClick={() => trackLink(PRESS)}>
          {formatMessage(navLabels.press)}
        </FooterLink>
        <FooterLink as={Link} to={COVERAGE}>
          {formatMessage(navLabels.coverage)}
        </FooterLink>
        <FooterLink as={Link} to={LICENSED_PARTNERS}>
          {formatMessage(navLabels.licensedPartners)}
        </FooterLink>
        <FooterLink
          as={Link}
          to={LEGAL_NOTICE}
          onClick={() => trackLink(LEGAL_NOTICE)}
        >
          {formatMessage(navLabels.legalNotice)}
        </FooterLink>
      </div>
    </MenuItems>
  );
};
